import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Select,
  Button,
  Typography,
  Table,
  Skeleton,
  Empty,
  Tag,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useGetEHRAppointmentTypes } from 'src/hooks/ehr';
import {
  useGetSuperadminPractitionerSettings,
  useUpdateSuperadminPractitionerSettings,
} from 'src/hooks/settings';
import {
  listTemplates,
  listTemplateOverrides,
} from 'src/modules/ambient_solo/store/actions/settings';
import { getPractitionersOrgLevel } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getFromStorage } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';
import OrganizationFilter from '../SuperAdmin/OrganizationFilter';
import { listTemplateOverridesOrgs } from 'src/modules/SuperAdmin/store/actions/templateoverrides.action';

const { Title, Text } = Typography;

const EHRAppointmentTypeMappingPage = () => {
  const dispatch = useDispatch();

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const [selectedOrg, setSelectedOrg] = useState(() => {
    try {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      if (savedOrg) {
        return JSON.parse(savedOrg);
      }
    } catch {
      // Invalid JSON in localStorage, ignore and use default
    }
    return null;
  });
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [mappings, setMappings] = useState({});

  const superadminHeaders = useMemo(() => {
    return selectedOrg?.org_uuid
      ? {
          'Impersonate-Org-UUID': selectedOrg.org_uuid,
          'Headless-Mode': true,
        }
      : undefined;
  }, [selectedOrg?.org_uuid]);

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedOrg && currentPractitioner?.org_uuid) {
      setSelectedOrg({
        value: currentPractitioner.org_id,
        label: currentPractitioner.org_name,
        org_uuid: currentPractitioner.org_uuid,
      });
    }
  }, [currentPractitioner, selectedOrg]);

  const { practitionersOrgLevel } = useSelector((state) => state?.doctorsState);
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const {
    templateList,
    isTemplateListLoading,
    isTemplateOverridesListLoading,
  } = useSelector((state) => state?.settingsState);

  const { templateOverridesList } = useSelector(
    (state) => state?.templateOverridesState
  );

  const isTemplatesLoading =
    isTemplateListLoading || isTemplateOverridesListLoading;

  const selectedOrgData = useMemo(() => {
    if (!selectedOrg) return null;

    const fullOrgData = getActiveOrganizationsList?.find(
      (org) => org?.org_uuid === selectedOrg.org_uuid
    );

    const ehrSystem = fullOrgData?.ehr_system || null;
    const ehrId = fullOrgData?.ehr_id || null;
    return {
      org_uuid: selectedOrg.org_uuid,
      org_name: selectedOrg.label,
      ehr_system: ehrSystem,
      ehr_id: ehrId,
      practice_id: ehrId,
    };
  }, [selectedOrg, selectedPractitioner, getActiveOrganizationsList]);

  const { data: appointmentTypesData, isLoading: isAppointmentTypesLoading } =
    useGetEHRAppointmentTypes(
      {
        ehr_system: selectedOrgData?.ehr_system || '',
        org_uuid: selectedOrgData?.org_uuid || '',
        ehr_id: selectedOrgData?.ehr_id || '',
        practice_id: selectedOrgData?.practice_id || '',
      },
      {
        enabled:
          !!selectedOrgData?.org_uuid &&
          !!selectedOrgData?.ehr_id &&
          !!selectedOrgData?.ehr_system,
      }
    );

  const { data: practitionerSettings, isLoading: isSettingsLoading } =
    useGetSuperadminPractitionerSettings(
      {
        practitioner_id: selectedPractitioner?.value,
        headers: superadminHeaders,
      },
      {
        enabled: !!selectedPractitioner?.value && !!selectedOrg?.org_uuid,
      }
    );

  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateSuperadminPractitionerSettings({ headers: superadminHeaders });

  const visibleAppointmentTypes = useMemo(() => {
    if (!appointmentTypesData?.appointment_types) return [];
    return appointmentTypesData.appointment_types.filter(
      (type) => type.hide !== 'yes' && !type.name?.includes('[DELETED]')
    );
  }, [appointmentTypesData]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrg(newOrg);
        setSelectedPractitioner(null);
        setMappings({});
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedOrg?.org_uuid) {
      dispatch(getPractitionersOrgLevel(selectedOrg.org_uuid));
      dispatch(listTemplates('in-visit', selectedOrg.org_uuid));
      dispatch(listTemplateOverridesOrgs(selectedOrg.org_uuid));
    }
  }, [selectedOrg?.org_uuid, dispatch]);

  useEffect(() => {
    if (!selectedPractitioner?.value) {
      setMappings({});
      return;
    }
  }, [selectedPractitioner?.value]);

  useEffect(() => {
    if (
      practitionerSettings?.note_preferences?.appointment_type_template_mapping
    ) {
      setMappings(
        practitionerSettings.note_preferences.appointment_type_template_mapping
      );
    } else {
      setMappings({});
    }
  }, [practitionerSettings]);

  const practitionerOptions = useMemo(() => {
    if (!practitionersOrgLevel) return [];
    return practitionersOrgLevel.map((p) => ({
      value: p.practitioner_id,
      label: p.practitioner_name,
      ehr_practitioner_id: p.ehr_practitioner_id,
    }));
  }, [practitionersOrgLevel]);

  const templateOptions = useMemo(() => {
    const options = [];
    if (templateOverridesList?.length > 0 && selectedPractitioner?.value) {
      const practitionerTemplates = templateOverridesList.filter(
        (t) => t.practitioner_id === selectedPractitioner.value
      );
      if (practitionerTemplates.length > 0) {
        options.push({
          label: 'Custom Templates',
          options: practitionerTemplates.map((t) => ({
            value: `override:${t.template_overrides_id}`,
            label: t.name,
          })),
        });
      }
    }
    if (templateList?.length > 0) {
      options.push({
        label: 'Pre-built Templates',
        options: templateList.map((t) => ({
          value: `template:${t.template_id}`,
          label: t.name,
        })),
      });
    }
    return options;
  }, [templateList, templateOverridesList, selectedPractitioner?.value]);

  const handleMappingChange = (
    appointmentTypeId,
    templateValue,
    templateName
  ) => {
    if (!templateValue) {
      const newMappings = { ...mappings };
      delete newMappings[appointmentTypeId];
      setMappings(newMappings);
      return;
    }

    const [type, id] = templateValue.split(':');
    const mappingValue =
      type === 'override'
        ? { template_overrides_id: id, template_name: templateName }
        : { template_id: id, template_name: templateName };

    setMappings({
      ...mappings,
      [appointmentTypeId]: mappingValue,
    });
  };

  const handleSave = () => {
    if (!selectedPractitioner?.value) return;
    updateSettings({
      practitioner_id: selectedPractitioner.value,
      settings: {
        appointment_type_template_mapping: mappings,
      },
    });
  };

  const getTemplateValue = (appointmentTypeId) => {
    const mapping = mappings[appointmentTypeId];
    if (!mapping) return undefined;
    if (mapping.template_overrides_id)
      return `override:${mapping.template_overrides_id}`;
    if (mapping.template_id) return `template:${mapping.template_id}`;
    return undefined;
  };

  const columns = [
    {
      title: 'EHR Appointment Type',
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      render: (name, record) => (
        <div className="flex items-center gap-2">
          {record.color && (
            <Tag color={record.color.toLowerCase()}>{record.color}</Tag>
          )}
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      title: '',
      key: 'arrow',
      width: '10%',
      render: () => <ArrowRightOutlined className="text-gray-400" />,
    },
    {
      title: 'Insight AI Template',
      dataIndex: 'id',
      key: 'template',
      width: '55%',
      render: (appointmentTypeId) => (
        <Select
          className="w-full"
          placeholder="Select template..."
          value={getTemplateValue(appointmentTypeId)}
          onChange={(value, option) =>
            handleMappingChange(appointmentTypeId, value, option?.label)
          }
          allowClear
          loading={isTemplatesLoading}
          options={templateOptions}
        />
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <OrganizationFilter />

      <div className="py-4">
        <Title level={3}>EHR Appointment Type Mapping</Title>
        <Text type="secondary">
          Map EHR appointment types to Insight AI templates for automatic
          template selection
        </Text>
      </div>

      <Card className="shadow-md bg-white rounded-xl mb-4">
        <div className="mb-6">
          <Title level={5}>Step 1: Select Practitioner</Title>
          <Text type="secondary" className="block mb-3">
            Choose a practitioner to configure their appointment type to
            template mappings
          </Text>
          <Select
            className="w-full max-w-md"
            placeholder="Select practitioner..."
            value={selectedPractitioner?.value}
            onChange={(value) => {
              const practitioner = practitionerOptions.find(
                (p) => p.value === value
              );
              setSelectedPractitioner(practitioner);
            }}
            options={practitionerOptions}
            loading={!practitionersOrgLevel}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Card>

      {selectedPractitioner && (
        <Card className="shadow-md bg-white rounded-xl">
          <div className="flex justify-between items-center mb-6">
            <div>
              <Title level={5} className="m-0">
                Step 2: Map Appointment Types to Templates
              </Title>
              <Text type="secondary">
                Assign templates to each appointment type for{' '}
                {selectedPractitioner.label}
              </Text>
            </div>
            <Button
              type="primary"
              className="bg-[#00D09C] border-[#00D09C] hover:bg-[#00b588] hover:border-[#00b588]"
              onClick={handleSave}
              loading={isUpdating}
              disabled={!selectedOrg?.org_uuid || !selectedPractitioner?.value}
            >
              Save Mappings
            </Button>
          </div>

          {isAppointmentTypesLoading || isSettingsLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : visibleAppointmentTypes.length > 0 ? (
            <Table
              dataSource={visibleAppointmentTypes}
              columns={columns}
              rowKey="id"
              pagination={false}
              className="appointment-type-mapping-table"
            />
          ) : (
            <Empty
              description={
                !selectedOrgData?.ehr_system
                  ? 'No EHR system configured for the selected organization'
                  : !selectedOrgData?.practice_id
                    ? 'No practice ID found for the selected practitioner'
                    : 'No appointment types found'
              }
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default EHRAppointmentTypeMappingPage;
